import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BackOfficeELearningService } from '../back-office-e-learning.service';
import { IMasterclass } from '@shared';
import { FirebaseService } from 'src/app/firebase/firebase.service';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { v4 as uuid } from 'uuid';
import { ELearningElementDialogOperation } from '../../models/ELearningElement';
import { COPIED_FEEDBACK_DURATION_MS, DELIVERABLE_VARIABLES_EMPTY_HINT } from '../../back-office-conf';
import { formatDeliverableQuestionVariablesAsList, formatDeliverableQuestionVariablesAsPrompt, IDeliverableQuestionVariableGroup, listDeliverableQuestionVariables } from '../../utils/deliverable-question-variables';


type DeliverableVariablesExport = 'list' | 'prompt';

@Component({
  selector: 'app-new-masterclass-form-dialog',
  templateUrl: './new-masterclass-form-dialog.component.html',
  styleUrls: ['./new-masterclass-form-dialog.component.scss']
})
export class NewMasterclassFormDialogComponent implements OnInit {
  newMasterclassForm = this.fb.group({
    name: ['', Validators.required],
    description: ['', Validators.required],
    speakerName: [''],
    speakerPicture: [null],
    category: ['', Validators.required],
    isAvailable: [false, Validators.required],
    isBuyable: [false, Validators.required],
    deliverablePrompt: ['']
  });

  loading = false;
  variableGroups: IDeliverableQuestionVariableGroup[] = [];
  copiedVariable: string | null = null;
  copiedExport: DeliverableVariablesExport | null = null;
  readonly emptyVariablesHint = DELIVERABLE_VARIABLES_EMPTY_HINT;

  constructor(@Inject(MAT_DIALOG_DATA) public data: { elementInfo: IMasterclass }, private fb: FormBuilder, public dialogRef: MatDialogRef<NewMasterclassFormDialogComponent>, private backOfficeELearningService: BackOfficeELearningService, private afStorage: AngularFireStorage, private firebaseService: FirebaseService) { }

  ngOnInit(): void {
    this.mapInfoToForm();
    this.variableGroups = listDeliverableQuestionVariables(this.data.elementInfo);
  }

  copyVariable(variable: string): void {
    navigator.clipboard.writeText(variable).then(() => {
      this.copiedVariable = variable;
      setTimeout(() => this.copiedVariable = null, COPIED_FEEDBACK_DURATION_MS);
    });
  }

  copyVariablesAsList(): void {
    this.copyExport('list', formatDeliverableQuestionVariablesAsList(this.variableGroups));
  }

  copyVariablesAsPrompt(): void {
    this.copyExport('prompt', formatDeliverableQuestionVariablesAsPrompt(this.data.elementInfo.name, this.variableGroups));
  }

  private copyExport(exportType: DeliverableVariablesExport, content: string): void {
    navigator.clipboard.writeText(content).then(() => {
      this.copiedExport = exportType;
      setTimeout(() => this.copiedExport = null, COPIED_FEEDBACK_DURATION_MS);
    });
  }

  mapInfoToForm() {
    const elementInfo: IMasterclass = (this.data.elementInfo as IMasterclass);
    if (elementInfo) {
      this.newMasterclassForm.patchValue({
        name: elementInfo.name,
        description: elementInfo.description,
        speakerName: elementInfo.speaker?.name,
        speakerPicture: elementInfo.speaker?.photoUrl ?? null,
        category: elementInfo.category,
        isAvailable: !elementInfo.isComingSoon,
        isBuyable: elementInfo.isBuyable,
        deliverablePrompt: elementInfo.deliverablePrompt ?? '',
      });
    }
  }

  onSpeakerPictureChange(event: Event | null) {
    const target = event?.target as HTMLInputElement;

    if (!target) {
      this.newMasterclassForm.patchValue({
        speakerPicture: null
      });
    } else {
      this.newMasterclassForm.patchValue({
        speakerPicture: target.files[0]
      });
    }
  }

  deleteMasterclass(): void {
    this.dialogRef.close({ element: { info: this.data.elementInfo, type: 'masterclass' }, operation: 'delete' });
  }

  async saveMasterclassWithOperation(operation: ELearningElementDialogOperation): Promise<void> {
    if (this.newMasterclassForm.valid) {
      this.loading = true;
      const formValue = this.newMasterclassForm.value;

      await this.firebaseService.downloadFileInFirebase(formValue.speakerPicture, '/masterclasses/speakers/', ['image/png', 'image/webp']).then((photoUrl) => {
        const finalPhotoUrl = photoUrl?.length ? photoUrl : formValue.speakerPicture ?? null;
        const masterclass: IMasterclass = {
          id: uuid(),
          modules: [],
          ...this.data.elementInfo,
          name: formValue.name,
          description: formValue.description,
          speaker: {
            id: uuid(),
            name: formValue.speakerName,
            photoUrl: finalPhotoUrl
          },
          category: formValue.category,
          isComingSoon: !formValue.isAvailable,
          isBuyable: formValue.isBuyable,
          deliverablePrompt: formValue.deliverablePrompt ?? '',
        }
        this.dialogRef.close({ element: { info: masterclass, type: 'masterclass' }, operation });
      }).catch((err) => {
        this.loading = false;
        alert(err);
      });
    }
  }
}
